import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, FileText, FileCheck, FlaskConical, Stethoscope, Camera, NotebookPen, User as UserIcon, Plus, X, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatError } from "../lib/api";
import { generatePrescriptionPDF } from "../lib/pdf";
import { generateExamOrderPDF } from "../lib/examPdf";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

function loadJitsi() {
  return new Promise((resolve, reject) => {
    if (window.JitsiMeetExternalAPI) return resolve(window.JitsiMeetExternalAPI);
    const s = document.createElement("script");
    s.src = "https://meet.jit.si/external_api.js";
    s.async = true;
    s.onload = () => resolve(window.JitsiMeetExternalAPI);
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

function PatientPanel({ patientId, settings }) {
  const [patient, setPatient] = useState(null);
  const [history, setHistory] = useState([]);
  useEffect(() => {
    if (!patientId) return;
    api.get(`/history/${patientId}`).then(({ data }) => setHistory(data || [])).catch(() => {});
    // Use doctor-visible /patients endpoint (require_role doctor/admin)
    api.get("/patients").then(({ data }) => {
      const p = (data || []).find((u) => u.id === patientId);
      if (p) setPatient(p);
    }).catch(() => {});
  }, [patientId]);
  return (
    <Card className="bg-white/5 border-white/10 text-white">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <UserIcon className="w-4 h-4" /> Datos del paciente
        </div>
        {patient ? (
          <div className="text-sm space-y-1">
            <div className="font-medium">{patient.name}</div>
            <div className="text-white/60 text-xs">{patient.email}</div>
            {patient.nif && <div className="text-white/60 text-xs">DNI: {patient.nif}</div>}
          </div>
        ) : <div className="text-white/50 text-xs">Cargando...</div>}
        <div className="border-t border-white/10 pt-3">
          <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Historia ({history.length})</div>
          <div className="max-h-40 overflow-y-auto space-y-2 text-xs">
            {history.slice(0, 6).map((h) => (
              <div key={h.id} className="border-l-2 border-emerald-500/50 pl-2">
                <div className="text-white/80">{(h.created_at || "").slice(0, 10)} · {h.title || "Entrada"}</div>
                <div className="text-white/50 line-clamp-2">{h.notes || h.description || ""}</div>
              </div>
            ))}
            {history.length === 0 && <div className="text-white/40">Sin historia previa</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickRx({ patientId, appointmentId, settings, doctor }) {
  const [diagnosis, setDiagnosis] = useState("");
  const [meds, setMeds] = useState([{ name: "", dosage: "", frequency: "", duration: "" }]);
  const [saving, setSaving] = useState(false);
  const setMed = (i, k, v) => setMeds((arr) => arr.map((m, idx) => idx === i ? { ...m, [k]: v } : m));
  const addMed = () => setMeds((m) => [...m, { name: "", dosage: "", frequency: "", duration: "" }]);
  const rmMed = (i) => setMeds((m) => m.filter((_, idx) => idx !== i));
  const submit = async () => {
    if (!diagnosis || !meds[0].name) { toast.error("Completa diagnóstico y al menos 1 medicamento"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/prescriptions", {
        patient_id: patientId, appointment_id: appointmentId || null,
        diagnosis, medications: meds.filter((m) => m.name), notes: "",
      });
      toast.success("Receta creada");
      await generatePrescriptionPDF({ ...data, _settings: settings, _doctor: doctor });
      setDiagnosis(""); setMeds([{ name: "", dosage: "", frequency: "", duration: "" }]);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3 text-white">
      <div>
        <Label className="text-white/80 text-xs">Diagnóstico</Label>
        <Textarea rows={2} value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} className="bg-white/5 border-white/10 text-white" data-testid="quick-rx-diagnosis" />
      </div>
      {meds.map((m, i) => (
        <div key={i} className="border border-white/10 rounded-lg p-2 space-y-2 bg-white/5">
          <div className="flex items-center justify-between">
            <div className="text-xs text-white/60">Medicamento {i + 1}</div>
            {meds.length > 1 && <Button size="icon" variant="ghost" className="h-6 w-6 text-white/60 hover:text-white" onClick={() => rmMed(i)}><X className="w-3 h-3" /></Button>}
          </div>
          <Input placeholder="Nombre" value={m.name} onChange={(e) => setMed(i, "name", e.target.value)} className="bg-white/5 border-white/10 text-white text-sm" data-testid={`quick-rx-med-name-${i}`} />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Dosis (ej. 500mg)" value={m.dosage} onChange={(e) => setMed(i, "dosage", e.target.value)} className="bg-white/5 border-white/10 text-white text-sm" />
            <Input placeholder="Frecuencia" value={m.frequency} onChange={(e) => setMed(i, "frequency", e.target.value)} className="bg-white/5 border-white/10 text-white text-sm" />
          </div>
          <Input placeholder="Duración (ej. 7 días)" value={m.duration} onChange={(e) => setMed(i, "duration", e.target.value)} className="bg-white/5 border-white/10 text-white text-sm" />
        </div>
      ))}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={addMed} className="border-white/20 text-white hover:bg-white/10"><Plus className="w-3 h-3 mr-1" /> Añadir</Button>
        <Button size="sm" onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="quick-rx-submit">
          {saving ? "..." : "Crear y descargar PDF"}
        </Button>
      </div>
    </div>
  );
}

function QuickExam({ type, patientId, appointmentId, settings, doctor }) {
  const [catalog, setCatalog] = useState([]);
  const [picked, setPicked] = useState([]);
  const [clinical, setClinical] = useState("");
  const [urgency, setUrgency] = useState("routine");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/exams/catalog").then(({ data }) => setCatalog(data?.[type] || []));
  }, [type]);
  const toggle = (name) => setPicked((p) => p.includes(name) ? p.filter((n) => n !== name) : [...p, name]);
  const submit = async () => {
    if (picked.length === 0) { toast.error("Selecciona al menos 1 prueba"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/exam-orders", {
        patient_id: patientId, appointment_id: appointmentId || null, type,
        tests: picked.map((n) => ({ name: n, notes: "" })),
        clinical_info: clinical, urgency, notes: "",
      });
      toast.success(`Petición ${type === "lab" ? "de laboratorio" : "de imagen"} creada`);
      await generateExamOrderPDF({ ...data, _settings: settings, _doctor: doctor });
      setPicked([]); setClinical("");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3 text-white">
      <div>
        <Label className="text-white/80 text-xs">Información clínica</Label>
        <Textarea rows={2} value={clinical} onChange={(e) => setClinical(e.target.value)} className="bg-white/5 border-white/10 text-white" />
      </div>
      <div>
        <Label className="text-white/80 text-xs">Urgencia</Label>
        <Select value={urgency} onValueChange={setUrgency}>
          <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="routine">Rutina</SelectItem>
            <SelectItem value="preferent">Preferente</SelectItem>
            <SelectItem value="urgent">Urgente</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-white/80 text-xs">Pruebas ({picked.length} seleccionadas)</Label>
        <div className="max-h-48 overflow-y-auto border border-white/10 rounded-lg p-2 bg-white/5 space-y-1">
          {catalog.map((c) => (
            <button key={c} onClick={() => toggle(c)} type="button"
              className={`w-full text-left text-xs px-2 py-1.5 rounded transition-colors ${picked.includes(c) ? "bg-emerald-600 text-white" : "text-white/80 hover:bg-white/10"}`}
              data-testid={`quick-${type}-test-${c}`}>
              {c}
            </button>
          ))}
        </div>
      </div>
      <Button size="sm" onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 w-full" data-testid={`quick-${type}-submit`}>
        {saving ? "..." : "Crear y descargar PDF"}
      </Button>
    </div>
  );
}

function FinishConsultationButton({ appointmentId }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  if (!appointmentId) return null;
  const send = async () => {
    if (!window.confirm("¿Enviar al paciente el resumen de la consulta por email (con recetas, exámenes y notas)?")) return;
    setSending(true);
    try {
      const { data } = await api.post(`/appointments/${appointmentId}/post-consultation-email`);
      const s = data.summary || {};
      toast.success(`Resumen enviado · ${s.prescriptions || 0} receta(s), ${s.lab || 0} lab, ${s.imaging || 0} imagen, ${s.notes || 0} nota(s)`);
      setSent(true);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSending(false); }
  };
  return (
    <Button onClick={send} disabled={sending || sent} className={`w-full ${sent ? "bg-emerald-800" : "bg-emerald-600 hover:bg-emerald-700"}`} data-testid="finish-consultation-btn">
      <Send className="w-4 h-4 mr-2" /> {sending ? "Enviando..." : sent ? "Resumen enviado" : "Finalizar y enviar resumen al paciente"}
    </Button>
  );
}

function QuickNotes({ patientId }) {
  const [title, setTitle] = useState("Notas de teleconsulta");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!notes.trim()) { toast.error("Escribe alguna nota"); return; }
    setSaving(true);
    try {
      await api.post("/history", { patient_id: patientId, title, content: notes, notes, type: "note" });
      toast.success("Notas guardadas en la historia clínica");
      setNotes("");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3 text-white">
      <div>
        <Label className="text-white/80 text-xs">Título</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-white/5 border-white/10 text-white" />
      </div>
      <div>
        <Label className="text-white/80 text-xs">Notas</Label>
        <Textarea rows={6} value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-white/5 border-white/10 text-white" data-testid="quick-notes-text" />
      </div>
      <Button size="sm" onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 w-full" data-testid="quick-notes-submit">
        {saving ? "..." : "Guardar en historia"}
      </Button>
    </div>
  );
}

export default function Teleconsulta({ mode = "video" }) {
  const { roomId, patientId: pathPatientId } = useParams();
  const [params] = useSearchParams();
  const isInPerson = mode === "in_person";
  const isPhone = mode === "phone";
  const room = roomId || params.get("room") || `drdimy-${Date.now()}`;
  const apptId = params.get("appt") || "";
  const patientIdQ = pathPatientId || params.get("pid") || "";
  const { user } = useAuth();
  const nav = useNavigate();
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const [settings, setSettings] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [patientId, setPatientId] = useState(patientIdQ);
  const [patient, setPatient] = useState(null);
  const [apptReason, setApptReason] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
    if (user?.role === "doctor") setDoctor(user);
    else api.get("/doctors").then(({ data }) => setDoctor(data?.[0])).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!apptId) return;
    api.get("/appointments").then(({ data }) => {
      const a = (data || []).find((x) => x.id === apptId);
      if (a) {
        if (!patientId) setPatientId(a.patient_id);
        if (a.reason) setApptReason(a.reason);
      }
    }).catch(() => {});
  }, [apptId, patientId]);

  useEffect(() => {
    if (!patientId) return;
    api.get("/patients").then(({ data }) => {
      const p = (data || []).find((x) => x.id === patientId);
      if (p) setPatient(p);
    }).catch(() => {});
  }, [patientId]);

  useEffect(() => {
    // Skip Jitsi entirely for in-person and phone consultations.
    if (isInPerson || isPhone) return;
    let mounted = true;
    loadJitsi().then((Jitsi) => {
      if (!mounted || !containerRef.current) return;
      apiRef.current = new Jitsi("meet.jit.si", {
        roomName: room,
        parentNode: containerRef.current,
        width: "100%", height: "100%",
        userInfo: { displayName: user?.name || "Invitado", email: user?.email || "" },
        configOverwrite: {
          prejoinPageEnabled: false,
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          disableDeepLinking: true,
        },
        interfaceConfigOverwrite: {
          MOBILE_APP_PROMO: false,
          SHOW_JITSI_WATERMARK: false,
          SHOW_BRAND_WATERMARK: false,
          DEFAULT_BACKGROUND: "#0F172A",
          TOOLBAR_BUTTONS: ["microphone", "camera", "desktop", "fullscreen", "hangup", "chat", "raisehand", "tileview", "settings"],
        },
      });
      apiRef.current.addEventListener("readyToClose", () => nav(-1));
    });
    return () => {
      mounted = false;
      try { apiRef.current?.dispose(); } catch (e) { /* ignore */ }
    };
  }, [room, user, nav, isInPerson, isPhone]);

  const isDoctor = user?.role === "doctor" || user?.role === "admin";

  return (
    <div className="min-h-screen flex flex-col bg-[#0F172A] -m-8" data-testid={isInPerson ? "consulta-presencial-page" : (isPhone ? "consulta-telefonica-page" : "teleconsulta-page")}>
      <header className="px-6 h-14 flex items-center justify-between border-b border-white/10 bg-[#0F172A] text-white">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={() => nav(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <div className="flex items-center gap-2 text-sm opacity-80">
            {isInPerson ? <Stethoscope className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
            {isInPerson ? "Consulta presencial" : (isPhone ? "Consulta telefónica" : <>Sala: <span className="font-mono text-xs">{room}</span></>)}
          </div>
          {isInPerson && <Badge variant="outline" className="border-amber-400/60 text-amber-200 bg-amber-500/10">PRESENCIAL</Badge>}
          {isPhone && <Badge variant="outline" className="border-sky-400/60 text-sky-200 bg-sky-500/10">TELEFÓNICA</Badge>}
          {!isInPerson && !isPhone && <Badge variant="outline" className="border-emerald-500/50 text-emerald-300 bg-emerald-500/10">EN VIVO</Badge>}
        </div>
        <div className="text-sm opacity-70">{user?.name}</div>
      </header>

      <div className="flex-1 flex">
        {/* Video Jitsi (only for telematic video mode) */}
        {!isInPerson && !isPhone && <div ref={containerRef} className="flex-1 min-w-0 bg-[#0F172A]" />}

        {/* Phone banner strip on the left for phone mode */}
        {isPhone && (
          <div className="hidden md:flex w-[360px] flex-shrink-0 items-center justify-center bg-gradient-to-br from-sky-950 via-slate-900 to-slate-950 text-white p-8">
            <div className="text-center space-y-4">
              <div className="w-24 h-24 mx-auto rounded-full bg-sky-500/20 border border-sky-400/40 flex items-center justify-center">
                <Phone className="w-12 h-12 text-sky-300" />
              </div>
              <div className="font-display text-2xl">Llamada telefónica</div>
              {patient?.phone ? (
                <>
                  <div className="text-sky-200 font-mono text-lg">{patient.phone}</div>
                  <a href={`tel:${patient.phone}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sky-500 hover:bg-sky-400 text-white font-medium" data-testid="call-patient-btn">
                    <Phone className="w-4 h-4" /> Llamar ahora
                  </a>
                </>
              ) : (
                <div className="text-sm text-white/60">El paciente no tiene un número de teléfono registrado.</div>
              )}
              <div className="pt-6 text-xs text-white/50">
                {patient?.name || "Paciente"}<br />
                Registre la consulta en el panel de la derecha (receta, análisis, notas...).
              </div>
            </div>
          </div>
        )}

        {/* Clinical panel: full width in presencial mode, sidebar in video mode */}
        {isDoctor && (
          <aside
            className={`${isInPerson || isPhone ? "flex-1" : "w-[420px] flex-shrink-0"} bg-[#0B1220] border-l border-white/10 overflow-y-auto`}
            data-testid={isInPerson ? "consulta-panel" : (isPhone ? "consulta-telefonica-panel" : "teleconsulta-sidepanel")}
          >
            <div className={`${isInPerson || isPhone ? "max-w-3xl mx-auto p-6" : "p-4"} space-y-4`}>
              {patientId ? (
                <>
                  <PatientPanel patientId={patientId} settings={settings} />
                  <Tabs defaultValue="rx" className="w-full">
                    <TabsList className="grid w-full grid-cols-4 bg-white/5">
                      <TabsTrigger value="rx" data-testid="tab-rx" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white"><Stethoscope className="w-3.5 h-3.5 mr-1" /> Receta</TabsTrigger>
                      <TabsTrigger value="lab" data-testid="tab-lab" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white"><FlaskConical className="w-3.5 h-3.5 mr-1" /> Análisis</TabsTrigger>
                      <TabsTrigger value="img" data-testid="tab-img" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white"><Camera className="w-3.5 h-3.5 mr-1" /> Imagen</TabsTrigger>
                      <TabsTrigger value="notes" data-testid="tab-notes" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white"><NotebookPen className="w-3.5 h-3.5 mr-1" /> Notas</TabsTrigger>
                    </TabsList>
                    <TabsContent value="rx" className="mt-4"><QuickRx patientId={patientId} appointmentId={apptId} settings={settings} doctor={doctor} /></TabsContent>
                    <TabsContent value="lab" className="mt-4"><QuickExam type="lab" patientId={patientId} appointmentId={apptId} settings={settings} doctor={doctor} /></TabsContent>
                    <TabsContent value="img" className="mt-4"><QuickExam type="imaging" patientId={patientId} appointmentId={apptId} settings={settings} doctor={doctor} /></TabsContent>
                    <TabsContent value="notes" className="mt-4"><QuickNotes patientId={patientId} /></TabsContent>
                  </Tabs>
                  <Button asChild variant="outline" className="w-full mt-2 bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white" data-testid="tele-generate-report">
                    <Link to="/app/informes" state={{ patientId, appointmentId: apptId, reason: apptReason }}>
                      <FileCheck className="w-4 h-4 mr-2" /> Generar informe de consulta
                    </Link>
                  </Button>
                  {apptId && <FinishConsultationButton appointmentId={apptId} />}
                </>
              ) : (
                <Card className="bg-white/5 border-white/10 text-white">
                  <CardContent className="p-4 text-sm text-white/70">
                    {isInPerson
                      ? "Selecciona un paciente desde Historia clínica para iniciar una consulta presencial."
                      : "Inicia la teleconsulta desde una cita para ver los datos del paciente y crear recetas/exámenes en vivo."}
                  </CardContent>
                </Card>
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
